import { INode } from 'shared/types/tree'
import { nodesOfTypeWithContent } from '../../utility/TreeHelpers'
import { NodeType } from 'shared/types/ingestion'
import { idsForNode, UpdateMethod, UpdateType } from './utility'
import { getStandardsByIds } from 'shared/hooks/standards/hooks'
import { Standard } from 'shared/types'
import { createNote } from '../TreeHelpers'
import { createIdMap, NodeIdMap } from '../../version_mapping/utility'
import { closestNavigableParent } from '../../utility/TreeNavigation'
import { NodeBasedUpdates, SkippedUpdates, UpdateNodeData } from './types'

export const getAllUpdates = async (
  tree: INode,
): Promise<{ updates: NodeBasedUpdates[]; skipping: SkippedUpdates[] }> => {
  const skillsLists = allSkillsLists(tree)
  const treeMap = createIdMap(tree)
  const map = await skillsMap(skillsLists)
  const skippedQuestions: INode[] = []
  const updates = skillsLists.reduce<{
    updates: NodeBasedUpdates[]
    skipping: SkippedUpdates[]
  }>(
    (acc, node) => {
      if (!node.leaves?.length) return acc
      const { node: parentNode } = treeMap[node.parent_id]
      if (parentNode.type === NodeType.Question) {
        // Handle Question type nodes differently
        skippedQuestions.push(parentNode)
        return acc
      }
      if (alreadyHasIt(node, map, treeMap)) {
        acc.skipping.push({
          node,
          extraInfo: `Parent Type: ${parentNode.type}`,
        })
        return acc
      }
      createNoteForSkillsList(node, map)
      acc.updates.push({
        node,
        extraInfo: `Parent Type: ${parentNode.type}`,
        updates: [noteUpdate(node, map, treeMap)],
      })
      return acc
    },
    { updates: [], skipping: [] },
  )
  if (!skippedQuestions.length) return updates
  const { updates: aUpdates, skipping } = getQuestionUpdates(
    skippedQuestions,
    treeMap,
    map,
  )
  updates.updates.push(...aUpdates)
  updates.skipping.push(...skipping)
  return updates
}

export function allSkillsLists(tree: INode) {
  return nodesOfTypeWithContent({
    tree,
    types: [NodeType.SkillsList, NodeType.WeightedSkillsList],
    content: [],
  })
}

function getQuestionUpdates(
  questions: INode[],
  treeMap: NodeIdMap,
  idMap: { [id: string]: Standard },
) {
  const activityHasIt = (activity: INode, skillNames: string[]) => {
    const node = [...activity.nodes].pop()!
    return isSkillsSearchNote(node, skillNames)
  }
  const activities = questions.reduce<INode[]>((acc, question) => {
    const activity = closestNavigableParent(question, treeMap)
    if (!acc.includes(activity)) acc.push(activity)
    return acc
  }, [])
  const extraInfo = 'Question skills at activity level'
  return activities.reduce<{
    updates: NodeBasedUpdates[]
    skipping: SkippedUpdates[]
  }>(
    ({ updates, skipping }, activity) => {
      const allNames = allSkillsLists(activity).reduce<string[]>(
        (acc, node) => {
          skillNames(node, idMap).forEach((name) => {
            if (!acc.includes(name)) acc.push(name)
          })
          return acc
        },
        [],
      )
      if (activityHasIt(activity, allNames)) {
        skipping.push({ node: activity, extraInfo })
      }
      updates.push({
        node: activity,
        updates: [
          {
            type: UpdateType.node,
            method: UpdateMethod.create,
            ids: {
              ...idsForNode(activity),
            },
            params: createNoteForSkillNames(allNames),
          },
        ],
        extraInfo,
      })
      return { updates, skipping }
    },
    { updates: [], skipping: [] },
  )
}

/* eslint-disable no-extend-native */
// @ts-ignore
String.prototype.includesAll = function (...strings: string[]) {
  return !strings.find((s) => !this.includes(s))
}

function alreadyHasIt(
  skillsList: INode,
  idMap: { [id: string]: Standard },
  treeMap: NodeIdMap,
) {
  const sibling = nextSibling(skillsList, treeMap)
  if (!sibling) return false
  return isSkillsSearchNote(sibling, skillNames(skillsList, idMap))
}

function isSkillsSearchNote(node: INode, skillNames: string[]) {
  if (node.type !== NodeType.Note) return false
  return node.body?.value.includesAll(...skillNames)
}

function nextSibling(node: INode, treeMap: NodeIdMap) {
  const { node: parentNode } = treeMap[node.parent_id]
  const nextIndex = parentNode.nodes.indexOf(node) + 1
  if (nextIndex < parentNode.nodes.length) return parentNode.nodes[nextIndex]
}

function createNoteForSkillNames(names: string[]) {
  const newNote = createNote({
    title: '',
    studentFacing: false,
    value: noteValueForNames(names),
  })
  return {
    ...newNote,
    body: {
      ...newNote.body,
      purpose: 'search',
    },
  }
}

function createNoteForSkillsList(
  skillsList: INode,
  idMap: { [id: string]: Standard },
) {
  const names = skillNames(skillsList, idMap)
  return createNoteForSkillNames(names)
}

function noteUpdate(
  skillsList: INode,
  idMap: { [id: string]: Standard },
  treeMap: NodeIdMap,
): UpdateNodeData {
  const { node: parentNode } = treeMap[skillsList.parent_id]
  return {
    type: UpdateType.node,
    method: UpdateMethod.create,
    ids: {
      ...idsForNode(parentNode),
      nextNodeId: nextSibling(skillsList, treeMap)?.id,
    },
    params: createNoteForSkillsList(skillsList, idMap),
  }
}

function skillIdsForList(node: INode) {
  return node.leaves?.map((leaf) => leaf.body.skillID) || []
}

async function skillsMap(skillsLists: INode[]) {
  const skillsIds = allSkillIds(...skillsLists)
  const { standards } = await getStandardsByIds(skillsIds)
  return standards.reduce<{ [id: string]: Standard }>((acc, skill) => {
    acc[skill.standardId] = skill
    return acc
  }, {})
}

export function allSkillIds(...skillsLists: INode[]) {
  const withDups = skillsLists.reduce<number[]>((acc, node) => {
    acc.push(...skillIdsForList(node))
    return acc
  }, [])
  return Array.from(new Set(withDups))
}

function skillNames(skillsList: INode, idMap: { [id: string]: Standard }) {
  return skillIdsForList(skillsList).reduce<string[]>((acc, id) => {
    if (id in idMap) {
      acc.push(idMap[id].standardName)
    }
    return acc
  }, [])
}

function noteValueForNames(allNames: string[]) {
  const joinedNames = allNames.join('; ')
  return `<div>\n<div><span style="color: #ffffff;">${joinedNames}</span></div>\n</div>`
}
